import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { AxiosError, AxiosInstance } from 'axios';
import { Separator } from '@radix-ui/react-separator';
import * as AlertDialog from '@radix-ui/react-alert-dialog';

import { errorToast } from '../../../../../../utils/toasts';
import { errorMessageAdapter } from '../../../../../../adapters';
import { ManagementService } from '../../../../../../services/ManagementService';
import { FormRejectDialog } from './FormRejectDialog';
import { useApi } from '../../../../../../services/hooks/apiHook';

type IDialogConfirmedCompany = {
  onDocumentApproved: { action: string; documentId?: number | undefined; openConfirmed: boolean };
  headers: any;
  documentId: number | string;
};

const DialogConfirmedCompany = ({ onDocumentApproved, headers, documentId }: IDialogConfirmedCompany) => {
  const [openDialogConfirmed, setOpenDialogConfirmed] = useState<boolean>(false);
  const [openDialogReject, setOpenDialogReject] = useState<boolean>(false);
  const [openApproved, setOpenApproved] = useState<boolean>(false);
  const [loading, setLoading] = React.useState(false);

  const api: AxiosInstance = useApi();

  const approvingTheDocument = async () => {
    const payload = {
      action: 'approved'
    };

    try {
      if (documentId) {
        setLoading(true);
        const response = await ManagementService.saveApprovedOrRejectDocument(api, documentId, payload, { headers });
        if (response.success === true) {
          setLoading(false);
          setOpenDialogConfirmed(false);
          setOpenApproved(false);
        }
      }
    } catch (error) {
      setLoading(false);
      errorToast(errorMessageAdapter(error as AxiosError));
    }
  };

  const handleFormReject = () => {
    setOpenDialogReject(true);
  };

  useEffect(() => {
    if (onDocumentApproved.action) {
      setOpenDialogConfirmed(onDocumentApproved.openConfirmed);
    }
  }, [onDocumentApproved]);

  return (
    <AlertDialog.Root open={openDialogConfirmed} onOpenChange={setOpenDialogConfirmed}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="fixed inset-0 bg-black bg-opacity-20 data-[state=open]:animate-overlayShow " />
        <AlertDialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white p-[25px] shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
          <AlertDialog.Title className="m-0 text-[17px] font-medium text-black">
            {t('others.othModalApproved')}
            <Separator className="my-[15px] bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
          </AlertDialog.Title>
          <AlertDialog.Description className="mb-5 mt-4 flex flex-col items-center justify-center text-[15px] leading-normal text-mauve11">
            <img src="/svg/like.svg" alt="icon check" className="mr-[10px]" />
            <span>{t('others.othAreYouSureApprove')}</span>
          </AlertDialog.Description>
          <div className="flex justify-center gap-[25px]">
            <AlertDialog.Cancel asChild>
              <button
                onClick={() => setOpenApproved(false)}
                className="hover:bg-mauve5inline-flex h-12 items-center justify-center rounded-[4px] border border-[#0C1FAA] px-[15px] font-medium leading-none text-[#0C1FAA] outline-none focus:shadow-[0_0_0_2px]"
              >
                {t('others.othCancel')}
              </button>
            </AlertDialog.Cancel>
            {onDocumentApproved.action === 'approved' ? (
              <button
                onClick={approvingTheDocument}
                className="hover:bg-red5 focus:shadow-red7 inline-flex h-12 items-center justify-center  rounded-[4px] bg-[#0C1FAA] px-[15px] font-medium leading-none text-[#fff] outline-none focus:shadow-[0_0_0_2px]"
              >
                {loading ? (
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-white" />
                ) : (
                  <div className="flex items-center">
                    <span>{t('others.othApprove')}</span>
                  </div>
                )}
              </button>
            ) : (
              <>
                <button
                  onClick={handleFormReject}
                  className="hover:bg-red5 focus:shadow-red7 inline-flex h-12 items-center justify-center  rounded-[4px] bg-[#0C1FAA] px-[15px] font-medium leading-none text-[#fff] outline-none focus:shadow-[0_0_0_2px]"
                >
                  {loading ? (
                    <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-white" />
                  ) : (
                    <div className="flex items-center">
                      <span>{t('others.othApprove')}</span>
                    </div>
                  )}
                </button>
                <FormRejectDialog
                  openDialogReject={openDialogReject}
                  documentId={documentId}
                  actionDialog={onDocumentApproved.action}
                  headers={headers}
                />
              </>
            )}
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
};

export default DialogConfirmedCompany;
